/**
 * Cliente do Worker para a superfície pública.
 *
 * O token vem no fragmento do URL (`#token`), não na query string: o fragmento
 * nunca é enviado ao servidor no pedido HTTP nem viaja no cabeçalho Referer
 * para terceiros. Daqui segue em cabeçalho próprio, não em URL.
 *
 * Nenhuma função deste ficheiro envia ids: o backend resolve tudo a partir do
 * token (§4). É por isso que `assinar()` só leva a assinatura.
 */

import {
	HEADER_TOKEN_PUBLICO,
	type AssinarPublicoPedido,
	type AssinarResposta,
	type ErroApi,
	type SessaoPublicaResposta
} from '@ac/shared';

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

/** Lido uma vez e removido do URL, para não ficar no histórico. */
const token = (() => {
	const t = location.hash.startsWith('#') ? location.hash.slice(1) : '';
	if (t) history.replaceState(null, '', location.pathname);
	return t;
})();

export const temToken = token.length > 0;

export class ErroDoServidor extends Error {
	constructor(
		readonly codigo: ErroApi['codigo'],
		mensagem: string
	) {
		super(mensagem);
	}
}

async function chamar<T>(caminho: string, init: RequestInit = {}): Promise<T> {
	const cabecalhos = new Headers(init.headers);
	cabecalhos.set('Content-Type', 'application/json');
	cabecalhos.set(HEADER_TOKEN_PUBLICO, token);

	let resposta: Response;
	try {
		resposta = await fetch(`${BASE}${caminho}`, { ...init, headers: cabecalhos });
	} catch {
		throw new ErroDoServidor('erro_interno', 'Sem ligação ao servidor');
	}

	const texto = await resposta.text();
	const corpo: unknown = texto ? JSON.parse(texto) : null;

	if (!resposta.ok) {
		const erro = corpo as ErroApi | null;
		throw new ErroDoServidor(erro?.codigo ?? 'erro_interno', erro?.erro ?? 'Erro inesperado');
	}

	return corpo as T;
}

export const api = {
	sessao: () => chamar<SessaoPublicaResposta>('/api/publico/sessao'),

	assinar: (assinaturaPng: string) => {
		const pedido: AssinarPublicoPedido = { assinaturaPng };
		return chamar<AssinarResposta>('/api/publico/assinar', {
			method: 'POST',
			body: JSON.stringify(pedido)
		});
	}
};
